import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@gigpro/ui/Collapsible";
import { ChevronDown, InfoOutline } from "@gigpro/ui/Icons";
import { Separator } from "@gigpro/ui/Separator";
import type { JobCreditStats } from "@sidegigllc/api/client";
import type React from "react";

export interface JobOfferPricing {
  postingCostDisplay: string;
  hireFeeDisplay: string;
  offerHireFeeDisplay: string;
  discount?: { label: string; valueDisplay: string };
  creditAppliedDisplay: string;
  totalDueTodayDisplay: string;
  noChargeDue: boolean;
}

interface PaymentSummaryProps {
  /** Absent for a direct hire, where the job is only created once the offer is confirmed. */
  jobDisplayId?: string;
  jobName: string;
  pricing: JobOfferPricing;
  credits: JobCreditStats;
}

const Row: React.FC<{ label: string; valueClassName?: string; children: React.ReactNode }> = ({
  label,
  valueClassName = "font-semibold text-black",
  children,
}) => (
  <div className="flex items-center justify-between text-normal">
    <span className="text-black">{label}</span>
    <span className={valueClassName}>{children}</span>
  </div>
);

const JobHeader: React.FC<{ jobDisplayId?: string; jobName: string }> = ({ jobDisplayId, jobName }) => (
  <p className="text-large text-black">
    {jobDisplayId ? (
      <>
        <span className="font-semibold">Job ID </span>
        <span>
          #{jobDisplayId} ({jobName})
        </span>
      </>
    ) : (
      <>
        <span className="font-semibold">Direct hire </span>
        <span>({jobName})</span>
      </>
    )}
  </p>
);

/** The offer summary for a Jobs plan location: no hire fee and no credits. */
export const JobsPlanPaymentSummary: React.FC<{ jobDisplayId?: string; jobName: string; message?: string }> = ({
  jobDisplayId,
  jobName,
  message,
}) => (
  <div className="flex flex-col gap-2 rounded-sm border border-light-gray bg-white p-5">
    <JobHeader jobDisplayId={jobDisplayId} jobName={jobName} />
    <Row label="Hire fee" valueClassName="font-semibold text-brand">
      $0 (included in your Jobs plan)
    </Row>
    {message && (
      <>
        <Separator className="my-1" />
        <p className="text-normal text-wrap text-black">{message}</p>
      </>
    )}
  </div>
);

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({ jobDisplayId, jobName, pricing, credits }) => {
  const willUseFreeCredit = credits.free_available > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 rounded-sm border border-light-gray bg-white p-5">
        <JobHeader jobDisplayId={jobDisplayId} jobName={jobName} />

        <Row label="Posting cost" valueClassName="font-semibold text-brand">
          {pricing.postingCostDisplay}
        </Row>

        <Row label="Hire fee">{pricing.hireFeeDisplay}</Row>

        <Separator className="my-1" />

        <p className="text-large font-semibold text-black">Credit &amp; charges</p>

        <Row label="Hire fee for this offer">{pricing.offerHireFeeDisplay}</Row>

        {pricing.discount && (
          <Row label={pricing.discount.label} valueClassName="font-semibold text-success">
            {pricing.discount.valueDisplay}
          </Row>
        )}

        {willUseFreeCredit && <Row label="Credit value applied">-{pricing.creditAppliedDisplay}</Row>}

        <Row label="Charge trigger">Offer accepted</Row>

        <Row label="Charge timing">Immediately upon offer acceptance</Row>

        <Separator className="my-1" />

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between text-large font-semibold">
            <span className="text-black">Total due today</span>
            <span data-free={pricing.noChargeDue || undefined} className="text-black data-free:text-info">
              {pricing.totalDueTodayDisplay}
            </span>
          </div>
          <p className="text-small text-wrap text-black">
            {pricing.noChargeDue
              ? "Note: No charge will be made to your payment method on file for this offer."
              : `Note: Payment method on file will be charged ${pricing.totalDueTodayDisplay} upon offer acceptance.`}
          </p>
        </div>
      </div>

      <Collapsible>
        <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between py-2">
          <div className="flex items-center gap-1">
            <InfoOutline className="size-4 text-info" />
            <span className="text-normal font-semibold text-info">Learn how credits work</span>
          </div>
          <ChevronDown className="size-4 text-black transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex flex-col gap-1 rounded-sm border border-tags-blue/50 bg-accent-light-blue p-3">
              <p className="text-normal font-semibold text-black">How credits work</p>
              <ul className="list-disc pl-5 text-normal/5 text-black">
                <li>Posting a job is free</li>
                <li>{pricing.offerHireFeeDisplay} charge only if the offer is accepted</li>
                <li>1 accepted offer uses 1 credit</li>
                <li>Declined offers do not consume credits</li>
              </ul>
            </div>

            <div className="flex flex-col gap-1 rounded-sm border border-light-gray bg-white p-3">
              <p className="text-normal font-semibold text-black">Credit summary</p>
              <Row label="Available (Ready to use)">{credits.available}</Row>
              <Row label="Pending (Waiting on offer)">{credits.pending}</Row>
              <Row label="Used (Accepted & billed)">{credits.used}</Row>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
